import React, { useState, useEffect, useCallback } from 'react';
import { PlatformChip } from './SocialEmbeds';

// ============================================
// Clip & Earn — submit SNUTZY clips, earn $10 per 1,000 verified views
// (route: /clip-and-earn)
// ============================================

const fmt = (n) => '$' + Math.round(parseFloat(n || 0)).toLocaleString();

const SUB_STATUS = {
  pending:  { label: 'In Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.35)' },
  active:   { label: 'Active',    color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.35)' },
  rejected: { label: 'Rejected',  color: '#f87171', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.35)' }
};

const PLATFORM_OPTIONS = [
  { value: 'youtube', label: 'YouTube', hint: 'https://youtube.com/watch?v=… or /shorts/…' },
  { value: 'twitter', label: 'X / Twitter', hint: 'https://x.com/you/status/…' },
  { value: 'tiktok', label: 'TikTok', hint: 'https://tiktok.com/@you/video/…' },
  { value: 'instagram', label: 'Instagram', hint: 'https://instagram.com/reel/…' }
];

export default function ClipEarnPage({ apiUrl, getToken, user, loginWithTwitch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ platform: 'youtube', url: '', title: '' });
  const [submitting, setSubmitting] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${apiUrl}/api/clips/mine`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Clips load error:', e);
    }
    setLoading(false);
  }, [apiUrl, getToken]);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  const submitClip = async () => {
    if (!form.url.trim()) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/clips/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(form)
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ type: 'success', text: 'Clip submitted! It will start earning once an admin verifies it.' });
        setForm({ platform: form.platform, url: '', title: '' });
        load();
      } else {
        setMessage({ type: 'error', text: body.error || 'Failed to submit clip' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to submit clip' });
    }
    setSubmitting(false);
  };

  const claim = async (submissionId) => {
    setClaimingId(submissionId);
    try {
      const res = await fetch(`${apiUrl}/api/clips/submissions/${submissionId}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage({ type: 'success', text: 'Earnings claimed! An admin will review the payout shortly.' });
        load();
      } else {
        setMessage({ type: 'error', text: body.error || 'Failed to claim' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to claim' });
    }
    setClaimingId(null);
  };

  const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '1.5rem' };
  const label = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: '#71717a' };
  const input = { padding: '0.6rem 0.9rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem', fontFamily: 'Outfit, sans-serif', width: '100%' };

  const rate = data?.ratePer1000 ?? 10;
  const hint = PLATFORM_OPTIONS.find(p => p.value === form.platform)?.hint;

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 className="page-title">Clip &amp; Earn</h1>
        <p className="page-subtitle">
          Edit and post SNUTZY content from the livestreams — earn <strong>${rate} per 1,000 verified views</strong>, on every clip.
        </p>
      </div>

      {/* Explainer */}
      <div style={{ ...card, marginBottom: '1.5rem' }}>
        <div style={{ ...label, color: '#c084fc', marginBottom: '0.8rem' }}>[ How it works ]</div>
        <div style={{ color: '#a1a1aa', fontSize: '0.92rem', lineHeight: 1.7 }}>
          <div>1. Clip moments from Snutzy's Twitch streams and post them on YouTube, X, TikTok or Instagram.</div>
          <div>2. Submit the link below — an admin verifies it's genuine SNUTZY content.</div>
          <div>3. Every <strong style={{ color: '#fff' }}>1,000 verified views</strong> unlocks <strong style={{ color: '#fbbf24' }}>${rate}</strong> — no cap, every clip keeps earning as it grows.</div>
        </div>
        <p style={{ color: '#71717a', fontSize: '0.8rem', margin: '0.9rem 0 0' }}>
          ${rate}/1,000 views is a promotional launch rate and may change. Views are <strong>manually verified</strong> by
          the team (YouTube counts update automatically).
        </p>
        <p style={{ color: '#f87171', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
          ⚠️ Any artificial view inflation (bots, view farms, engagement pods) will disqualify you from all rewards.
        </p>
      </div>

      {!user ? (
        <div style={{ ...card, textAlign: 'center', maxWidth: '440px', margin: '0 auto' }}>
          <p style={{ color: '#a1a1aa', marginBottom: '1.2rem' }}>Login to submit clips and track your earnings.</p>
          <button className="twitch-login-btn" onClick={loginWithTwitch} style={{ margin: '0 auto' }}>Login with Twitch</button>
        </div>
      ) : (
        <>
          {message && (
            <div style={{
              padding: '0.8rem 1.2rem', borderRadius: '10px', marginBottom: '1.2rem', fontSize: '0.9rem', fontWeight: 600,
              background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
              color: message.type === 'success' ? '#4ade80' : '#fca5a5',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem'
            }}>
              {message.text}
              <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1rem' }}>×</button>
            </div>
          )}

          {/* Submission form */}
          <div style={{ ...card, marginBottom: '1.5rem' }}>
            <div style={{ ...label, marginBottom: '0.9rem' }}>Submit a clip</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.8rem', marginBottom: '0.8rem' }}>
              <select value={form.platform} onChange={e => setForm(prev => ({ ...prev, platform: e.target.value }))} style={input}>
                {PLATFORM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <input
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Clip title (optional)"
                style={input}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <input
                value={form.url}
                onChange={e => setForm(prev => ({ ...prev, url: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') submitClip(); }}
                placeholder={hint}
                style={{ ...input, flex: 1, minWidth: '240px', width: 'auto' }}
              />
              <button
                onClick={submitClip}
                disabled={submitting || !form.url.trim()}
                style={{ padding: '0.6rem 1.6rem', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: submitting || !form.url.trim() ? 0.6 : 1 }}
              >
                {submitting ? 'Submitting…' : 'Submit Clip'}
              </button>
            </div>
          </div>

          {/* My submissions */}
          <div style={card}>
            <div style={{ ...label, marginBottom: '1rem' }}>My clips ({data?.submissions.length ?? 0})</div>
            {loading ? (
              <p style={{ color: '#71717a', margin: 0 }}>Loading…</p>
            ) : (!data || data.submissions.length === 0) ? (
              <p style={{ color: '#71717a', fontSize: '0.9rem', margin: 0 }}>No clips yet — submit your first one above!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                {data.submissions.map(sub => {
                  const st = SUB_STATUS[sub.status] || SUB_STATUS.pending;
                  const eligible = sub.earnings.eligible;
                  const claimed = sub.earnings.claimed;
                  const approved = sub.earnings.approved;
                  const denied = sub.earnings.denied;
                  return (
                    <div key={sub.id} style={{ padding: '1rem 1.1rem', borderRadius: '12px', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        <PlatformChip platform={sub.platform} />
                        <a href={sub.url} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', textDecoration: 'none', flex: 1, minWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sub.title || sub.url}
                        </a>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px', background: st.bg, border: `1px solid ${st.border}`, color: st.color }}>
                          {st.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', fontSize: '0.82rem', color: '#a1a1aa' }}>
                        <span>👁️ <strong style={{ color: '#fff' }}>{(sub.verified_views || 0).toLocaleString()}</strong> verified views</span>
                        {approved && <span style={{ color: '#4ade80' }}>✅ {fmt(approved.total)} paid</span>}
                        {claimed && <span style={{ color: '#fbbf24' }}>⏳ {fmt(claimed.total)} in review</span>}
                        {denied && <span style={{ color: '#fca5a5' }} title={denied.adminResponse || ''}>❌ {fmt(denied.total)} denied</span>}
                        {eligible && sub.status === 'active' && (
                          <button
                            onClick={() => claim(sub.id)}
                            disabled={claimingId === sub.id}
                            style={{ padding: '0.35rem 0.9rem', borderRadius: '999px', border: 'none', background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#1c1205', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: claimingId === sub.id ? 0.6 : 1 }}
                          >
                            Claim {fmt(eligible.total)}
                          </button>
                        )}
                      </div>
                      {sub.admin_notes && sub.status === 'rejected' && (
                        <p style={{ color: '#fca5a5', fontSize: '0.78rem', margin: '0.5rem 0 0' }}>Admin: {sub.admin_notes}</p>
                      )}
                      {denied?.adminResponse && (
                        <p style={{ color: '#fca5a5', fontSize: '0.78rem', margin: '0.5rem 0 0' }}>Denial reason: {denied.adminResponse}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
